import type { components } from "@/api-schema";
import type { CustomerGender } from "@/api-schema-enums";
import { getGenderLabel } from "@/common/helpers/customer-display";
import { formatApiDateOnlyToLocale } from "@/common/helpers/format-date";
import { isoToCountryName, isoToLanguageName } from "@/common/helpers/iso-display-name";
import { ContactLink } from "@/components/contact-link/contact-link";
import { AddressBlock } from "@/components/detail-page/_components/address-block";
import { DetailTabCard } from "@/components/detail-page/_components/tab/detail-tab-card";
import { DetailTabGrid } from "@/components/detail-page/_components/tab/detail-tab-grid";
import { DetailTabRow } from "@/components/detail-page/_components/tab/detail-tab-row";
import { RelationLink } from "@/components/relation-link/relation-link";
import { EmptyValue } from "@/components/ui/empty-value";
import { Building2, MailIcon, MapPin, PhoneIcon, PrinterIcon, SmartphoneIcon, UserRound } from "lucide-react";
import { useTranslation } from "react-i18next";

type Customer = components["schemas"]["Customer"];

export function OverviewTab({ customer }: { customer: Customer }) {
  const { t } = useTranslation(["masterdata", "common"]);

  const fullName = [customer.firstName, customer.lastName].filter(Boolean).join(" ");

  return (
    <DetailTabGrid>
      <DetailTabCard icon={UserRound} title={t("masterdata:CustomerInformation")}>
        <DetailTabRow label={t("common:Name")} value={fullName} />
        <DetailTabRow label={t("masterdata:Gender")} value={getGenderLabel(customer.gender as CustomerGender)} />
        <DetailTabRow label={t("masterdata:BirthDate")} value={formatApiDateOnlyToLocale(customer.birthDate)} />
        <DetailTabRow label={t("masterdata:CustomerCompanyName")} value={customer.customerCompanyName} />
        <DetailTabRow label={t("masterdata:VatId")} value={customer.vatId} mono />
        <DetailTabRow label={t("masterdata:CountryCode")} value={isoToCountryName(customer.countryCode)} />
        <DetailTabRow label={t("masterdata:LanguageCode")} value={isoToLanguageName(customer.languageCode)} />
      </DetailTabCard>

      <DetailTabCard icon={PhoneIcon} title={t("masterdata:ContactInformation")}>
        <DetailTabRow
          label={t("common:Email")}
          value={
            customer.email ? (
              <ContactLink icon={MailIcon} label={customer.email} href={`mailto:${customer.email}`} />
            ) : undefined
          }
        />
        <DetailTabRow
          label={t("common:Mobile")}
          value={
            customer.mobile ? (
              <ContactLink icon={SmartphoneIcon} label={customer.mobile} href={`tel:${customer.mobile}`} />
            ) : undefined
          }
        />
        <DetailTabRow
          label={t("masterdata:Landline")}
          value={
            customer.landline ? (
              <ContactLink icon={PhoneIcon} label={customer.landline} href={`tel:${customer.landline}`} />
            ) : undefined
          }
        />
        <DetailTabRow
          label={t("masterdata:Fax")}
          value={
            customer.fax ? (
              <ContactLink icon={PrinterIcon} label={customer.fax} href={`fax:${customer.fax}`} />
            ) : undefined
          }
        />
      </DetailTabCard>

      <DetailTabCard icon={Building2} title={t("common:Organization")}>
        <DetailTabRow
          label={t("common:Company")}
          value={
            customer.companies && customer.companies.length > 0 ? (
              <span className="flex flex-col gap-1">
                {customer.companies.map((company) => (
                  <RelationLink key={company.uuid} subject="Company" uuid={company.uuid} className="w-fit">
                    {company.name}
                  </RelationLink>
                ))}
              </span>
            ) : undefined
          }
        />
        <DetailTabRow
          label={t("common:Branch")}
          value={
            customer.branch ? (
              <RelationLink subject="Branch" uuid={customer.branch.uuid}>
                {customer.branch.name}
              </RelationLink>
            ) : undefined
          }
        />
      </DetailTabCard>

      <DetailTabCard icon={MapPin} title={t("masterdata:Addresses")} className="lg:col-span-2">
        {customer.addresses.length > 0 ? (
          <div className="grid grid-cols-1 gap-6 pt-1.5 sm:grid-cols-2">
            {customer.addresses.map((customerAddress) => (
              <AddressBlock
                key={customerAddress.uuid}
                typeLabel={t(`masterdata:AddressType${customerAddress.type}`)}
                address={customerAddress.address}
              />
            ))}
          </div>
        ) : (
          <div className="py-1.5">
            <EmptyValue />
          </div>
        )}
      </DetailTabCard>
    </DetailTabGrid>
  );
}
